// 音声エージェント（Gemini Live）へ公開する関数宣言とディスパッチ（純関数・ブラウザ非依存）。
//
// 役割: Gemini に渡す functionDeclarations の定義と、toolCall を実装へ振り分ける処理を持つ。
//       公開するのは「UI 操作」だけで、DuckDB / レイヤー操作などアプリのツールは一切渡さない。
//       空間処理は従来どおり Claude エージェントが担当し、送信ボタンを押すのは人間。
// 関係: useVoiceSession がハンドラ（入力欄への書き込み・地図キャプチャ）を注入して使う。
//       副作用をハンドラ側へ追い出しているため、ディスパッチだけを node --test で検証できる。
//       例外を投げずに { ok:false, error } を返すのは runtime.js（ツール例外を tool_result で
//       モデルへ返し自己修正させる）と同じ方針。

export const FILL_PROMPT = 'fill_prompt'
export const CAPTURE_MAP = 'capture_map'

// Gemini へ渡す関数宣言。型は OpenAPI 準拠の大文字表記（@google/genai の Type enum と同じ値）。
export const VOICE_FUNCTION_DECLARATIONS = [
  {
    name: FILL_PROMPT,
    description:
      'Claude エージェントへのプロンプト入力欄に、指示文の全文を書き込む（既存の内容は上書きされる）。' +
      'この関数は入力までしか行わない。実行はユーザーが画面の送信ボタンを押したときに初めて起こる。',
    parameters: {
      type: 'OBJECT',
      properties: {
        text: {
          type: 'STRING',
          description:
            'Claude エージェントへ渡す指示文の全文。日本語で、対象レイヤーの ID・処理内容・距離や単位・' +
            '出力レイヤー名が分かるように自己完結させる。会話の相槌は入れない。',
        },
      },
      required: ['text'],
    },
  },
  {
    name: CAPTURE_MAP,
    description:
      '現在画面に表示されている地図のスクリーンショットを取得して自分で見る。' +
      '「これ」「ここ」「この辺」など画面を指す言い方が出たときや、表示状態を確認したいときに使う。' +
      '1 回につき 1 枚しか届かないので、連続して呼ばない。',
    parameters: {
      type: 'OBJECT',
      properties: {
        reason: { type: 'STRING', description: '地図を見たい理由（1 文）' },
      },
    },
  },
]

// live.connect の config.tools にそのまま渡せる形。
export const VOICE_TOOLS = [{ functionDeclarations: VOICE_FUNCTION_DECLARATIONS }]

// toolCall の 1 件を実装へ振り分け、sendToolResponse へ渡せる functionResponse を返す。
// ハンドラは { fill_prompt: async (args) => object, capture_map: async (args) => object } の形で注入する。
// ハンドラの戻り値がそのまま response になる（未返却なら { ok: true }）。
export async function dispatchFunctionCall(call, handlers = {}) {
  const { id, name, args } = call ?? {}
  try {
    const handler = handlers[name]
    if (!handler) throw new Error(`未対応の関数です: ${name}`)
    if (name === FILL_PROMPT && typeof args?.text !== 'string') {
      throw new Error('text が指定されていません')
    }
    const response = await handler(args ?? {})
    return { id, name, response: response ?? { ok: true } }
  } catch (e) {
    return { id, name, response: { ok: false, error: String(e?.message ?? e) } }
  }
}

// toolCall メッセージ（functionCalls 配列）を順に処理する。
// gemini-3.1-flash-live-preview は非同期 function calling 非対応のため逐次で回す。
export async function dispatchToolCall(toolCall, handlers) {
  const calls = toolCall?.functionCalls ?? []
  const responses = []
  for (const call of calls) {
    responses.push(await dispatchFunctionCall(call, handlers))
  }
  return responses
}
